/**
 * Complete `LiveGameState` snapshots for the design lab.
 *
 * These are not test doubles: each one is a state the REDUCER could have
 * produced, so applying it with `setState(fixture, true)` and then feeding the
 * store a real `player_turn_response` behaves exactly as it would in a game
 * (`fixtures.test.ts` asserts that for every fixture).
 *
 * The cast is the design's: me = Vidya D (1482), opponent = Rahul K (1510),
 * table 4242, 3 + 0 Rated Blitz, and the Italian Game position of the L4
 * artboard after 14. Ne5.
 */
import {LiveGameState, START_FEN, initialState} from '@/Store/liveGame/state';

export const LAB_ME = '1001';
export const LAB_OPPONENT = '2002';
export const LAB_TABLE = 4242;
export const LAB_TC_MS = 180000;

/** the L4 position: after 14. Ne5, black to move */
export const ITALIAN_FEN =
  'r1bq1rk1/p3nppp/1pp5/3nN3/P1BP4/1Q6/1P1N1PPP/R3R1K1 b - - 1 14';
export const ITALIAN_PREVIOUS_FEN =
  'r1bq1rk1/p3nppp/1pp5/3n4/P1BP4/1Q3N2/1P1N1PPP/R3R1K1 w - - 0 14';
/** the position before 13...b6, i.e. one ply further back */
export const ITALIAN_FEN_25 =
  'r1bq1rk1/pp2nppp/2p5/3n4/P1BP4/1Q3N2/1P1N1PPP/R3R1K1 b - - 0 13';

export const ITALIAN_MOVES = [
  'e4',
  'e5',
  'Nf3',
  'Nc6',
  'Bc4',
  'Bc5',
  'c3',
  'Nf6',
  'd4',
  'exd4',
  'cxd4',
  'Bb4+',
  'Bd2',
  'Bxd2+',
  'Nbxd2',
  'd5',
  'exd5',
  'Nxd5',
  'Qb3',
  'Nce7',
  'O-O',
  'O-O',
  'Rfe1',
  'c6',
  'a4',
  'b6',
  'Ne5',
];

const session = {
  me: LAB_ME,
  displayName: 'Vidya D',
  customRules: [] as string[],
  isStaff: true,
  rating: 1482,
  isBeginner: false,
  lobbyVariant: null,
  connected: true,
  anotherTabActive: false,
};

const base = (): LiveGameState => ({
  ...initialState,
  session: {...session},
  lobby: {...initialState.lobby},
  game: {...initialState.game},
  assisted: {...initialState.assisted},
  challenge: {...initialState.challenge},
  tournament: {...initialState.tournament},
  watch: {...initialState.watch},
  meta: {...initialState.meta},
});

/** a live 3+0 game with me (BLACK) to move, as the L4 artboard shows it */
const liveBase = (now: number): LiveGameState => {
  const state = base();
  return {
    ...state,
    game: {
      ...state.game,
      phase: 'playing',
      tableId: LAB_TABLE,
      // I am black and it is my move (design: "1510 · black" on top is the
      // opponent, "1482 · white · your move" at the bottom is me — the lab
      // uses me = black so both clock faces are exercised).
      whiteId: LAB_OPPONENT,
      blackId: LAB_ME,
      myColor: 'black',
      opponentId: LAB_OPPONENT,
      opponentName: 'Rahul K',
      opponentRating: 1510,
      myRating: 1482,
      startFen: START_FEN,
      fen: ITALIAN_FEN,
      previousFen: ITALIAN_PREVIOUS_FEN,
      turnUserId: LAB_ME,
      moves: [...ITALIAN_MOVES],
      // design: opponent (white) 1:58, me (black) 2:41 and running
      whiteTimeMs: 118000,
      blackTimeMs: 161000,
      clockRunningSince: now,
      tcMs: LAB_TC_MS,
      incMs: 0,
      gamePlayMode: 'Play',
      gameDetails: '3 + 0 • Rated • Blitz',
      showAbort: false,
      origin: 'live',
    },
  };
};

const endedBase = (now: number): LiveGameState => {
  const live = liveBase(now);
  return {
    ...live,
    game: {
      ...live.game,
      phase: 'ended',
      clockRunningSince: null,
      turnUserId: null,
      showAbort: false,
    },
    lobby: {...live.lobby, phase: 'idle', seekTableId: null},
  };
};

export type FixtureName =
  | 'home'
  | 'seeking'
  | 'seekingNotFound'
  | 'matched'
  | 'liveMyMove'
  | 'liveOpponentMove'
  | 'liveDrawOffered'
  | 'liveDrawSent'
  | 'liveLowTime'
  | 'liveThinking'
  | 'liveAbortable'
  | 'resultWon'
  | 'resultLost'
  | 'resultDraw'
  | 'resultAborted';

export const FIXTURE_NAMES: FixtureName[] = [
  'home',
  'seeking',
  'seekingNotFound',
  'matched',
  'liveMyMove',
  'liveOpponentMove',
  'liveDrawOffered',
  'liveDrawSent',
  'liveLowTime',
  'liveThinking',
  'liveAbortable',
  'resultWon',
  'resultLost',
  'resultDraw',
  'resultAborted',
];

export const buildFixture = (
  name: FixtureName,
  now: number = Date.now(),
): LiveGameState => {
  switch (name) {
    case 'home':
      return base();

    case 'seeking': {
      const state = base();
      return {
        ...state,
        lobby: {
          ...state.lobby,
          phase: 'seeking',
          seekTableId: LAB_TABLE,
          tcMs: LAB_TC_MS,
          incMs: 0,
          gamePlayMode: 'Play',
        },
      };
    }

    // the seek gave up after 15 s (actions.seekExpired): the lobby is idle
    // again and the card shows "no opponent found"
    case 'seekingNotFound': {
      const state = base();
      return {
        ...state,
        lobby: {
          ...state.lobby,
          phase: 'idle',
          seekTableId: null,
          tcMs: LAB_TC_MS,
          incMs: 0,
          gamePlayMode: 'Play',
          playerNotFound: true,
        },
      };
    }

    case 'matched': {
      const live = liveBase(now);
      return {
        ...live,
        game: {
          ...live.game,
          phase: 'starting',
          fen: START_FEN,
          previousFen: null,
          moves: [],
          turnUserId: LAB_OPPONENT,
          whiteTimeMs: LAB_TC_MS,
          blackTimeMs: LAB_TC_MS,
          clockRunningSince: null,
          readyAt: now,
          showAbort: true,
          firstMoveDeadlineMs: now + 20000,
          // the window's LENGTH: black's copy of it is re-armed from this when
          // white's first move lands (reducers/game.ts)
          firstMoveSec: 20,
        },
      };
    }

    case 'liveMyMove':
      return liveBase(now);

    case 'liveOpponentMove': {
      // one ply back, so the side to move in the FEN really IS the opponent
      // (white) — the "Opponent moves" button replays from `game.fen`.
      const live = liveBase(now);
      return {
        ...live,
        game: {
          ...live.game,
          fen: ITALIAN_PREVIOUS_FEN,
          previousFen: ITALIAN_FEN_25,
          moves: ITALIAN_MOVES.slice(0, ITALIAN_MOVES.length - 1),
          turnUserId: LAB_OPPONENT,
        },
      };
    }

    case 'liveDrawOffered': {
      const live = liveBase(now);
      return {...live, game: {...live.game, drawOffered: true}};
    }

    case 'liveDrawSent': {
      const live = liveBase(now);
      return {...live, game: {...live.game, drawSent: true}};
    }

    case 'liveLowTime': {
      const live = liveBase(now);
      return {...live, game: {...live.game, blackTimeMs: 9400}};
    }

    case 'liveThinking': {
      const live = liveBase(now);
      return {
        ...live,
        // the gate only bites for a rule holder in casual play
        session: {...live.session, customRules: ['thinking_time_rule']},
        game: {...live.game, thinkingGateUntil: now + 42000},
      };
    }

    case 'liveAbortable': {
      const live = liveBase(now);
      return {
        ...live,
        game: {
          ...live.game,
          fen: START_FEN,
          previousFen: null,
          moves: [],
          turnUserId: LAB_OPPONENT,
          whiteTimeMs: LAB_TC_MS,
          blackTimeMs: LAB_TC_MS,
          showAbort: true,
          firstMoveDeadlineMs: now + 18000,
          firstMoveSec: 18,
        },
      };
    }

    case 'resultWon': {
      const ended = endedBase(now);
      return {
        ...ended,
        game: {
          ...ended.game,
          result: {
            key: `${LAB_TABLE}:checkmate:${LAB_ME}`,
            endType: 'checkmate',
            winner: LAB_ME,
            isDraw: false,
            outcome: 'Win',
            message: 'Congratulations! You won the match!',
            myRating: 1494,
            myRatingDelta: 12,
            opponentRating: 1498,
            opponentRatingDelta: -12,
            gameUuid: 'lab-game-uuid-mine',
            opponentGameUuid: 'lab-game-uuid-theirs',
            pgn: '',
            moves: [...ITALIAN_MOVES],
            finalFen: ITALIAN_FEN,
            endedAt: now,
            modalDelayMs: 1500,
          },
          endAckNeeded: false,
        },
      };
    }

    case 'resultLost': {
      const ended = endedBase(now);
      return {
        ...ended,
        game: {
          ...ended.game,
          result: {
            key: `${LAB_TABLE}:resign:${LAB_OPPONENT}`,
            endType: 'resign',
            winner: LAB_OPPONENT,
            isDraw: false,
            outcome: 'Loss',
            message: 'Sorry, you lost the match.',
            myRating: 1473,
            myRatingDelta: -9,
            opponentRating: 1519,
            opponentRatingDelta: 9,
            gameUuid: 'lab-game-uuid-mine',
            opponentGameUuid: 'lab-game-uuid-theirs',
            pgn: '',
            moves: [...ITALIAN_MOVES],
            finalFen: ITALIAN_FEN,
            endedAt: now,
            modalDelayMs: 0,
          },
          endAckNeeded: false,
        },
      };
    }

    case 'resultDraw': {
      const ended = endedBase(now);
      return {
        ...ended,
        game: {
          ...ended.game,
          result: {
            key: `${LAB_TABLE}:repetition:00`,
            endType: 'repetition',
            winner: null,
            isDraw: true,
            outcome: 'Draw',
            message: 'The match ended in a draw.',
            myRating: 1482,
            myRatingDelta: 0,
            opponentRating: 1510,
            opponentRatingDelta: 0,
            gameUuid: 'lab-game-uuid-mine',
            opponentGameUuid: 'lab-game-uuid-theirs',
            pgn: '',
            moves: [...ITALIAN_MOVES],
            finalFen: ITALIAN_FEN,
            endedAt: now,
            modalDelayMs: 0,
          },
          endAckNeeded: false,
        },
      };
    }

    case 'resultAborted': {
      const ended = endedBase(now);
      return {
        ...ended,
        game: {
          ...ended.game,
          fen: START_FEN,
          previousFen: null,
          moves: [],
          result: {
            key: `${LAB_TABLE}:abort:0`,
            endType: 'abort',
            winner: null,
            isDraw: false,
            outcome: 'Abort',
            message: 'The match was aborted.',
            myRating: 1482,
            myRatingDelta: 0,
            opponentRating: 1510,
            opponentRatingDelta: 0,
            gameUuid: '',
            opponentGameUuid: '',
            pgn: '',
            moves: [],
            finalFen: START_FEN,
            endedAt: now,
            modalDelayMs: 0,
          },
          endAckNeeded: false,
        },
      };
    }

    default:
      return base();
  }
};

/** human labels for the lab's fixture chips */
export const FIXTURE_LABELS: {[K in FixtureName]: string} = {
  home: 'Home',
  seeking: 'Seeking',
  seekingNotFound: 'Seeking · not found',
  matched: 'Matched',
  liveMyMove: 'Live · my move',
  liveOpponentMove: 'Live · their move',
  liveDrawOffered: 'Draw offered',
  liveDrawSent: 'Draw sent',
  liveLowTime: 'Low time',
  liveThinking: 'Thinking gate',
  liveAbortable: 'Abortable',
  resultWon: 'Result · won',
  resultLost: 'Result · lost',
  resultDraw: 'Result · draw',
  resultAborted: 'Result · aborted',
};
